import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useIsomorphicLayoutEffect } from 'react-use';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

/** A single photo has nowhere to loop to. */
const canLoop = (amountOfSlides: number) => amountOfSlides > 1;

/*
 * How long the strip has to be still before it counts as settled. Momentum and snapping both fire a
 * scroll event every frame, so a gap this long only happens once the swipe is over. `scrollend`
 * would say it directly, but Safari — most of the phones this is for — does not fire it.
 */
const SETTLE_DELAY_MS = 150;

/*
 * Photos the strip holds ready ahead of the one it shows. The first slide plus this many is what the
 * hero opens with, and swiping keeps the same reach ahead; the rest of the strip is a placeholder
 * until the reader gets near it, so a 100-photo hotel does not ship 100 photo sources up front.
 */
const PHOTOS_AHEAD = 5;

/** Where the strip sits, in slides: they are as wide as the strip, so its width is a slide's. */
const getSlidePosition = (container: HTMLDivElement) => {
  const slideWidth = container.getBoundingClientRect().width;
  return slideWidth > 0 ? container.scrollLeft / slideWidth : 0;
};

/**
 * The slide the first photo sits on: the one after the copy of the last photo, when that copy opens
 * the strip (MPH-1237).
 */
export const firstPhotoSlide = (hasLeadingCopy: boolean) => (hasLeadingCopy ? 1 : 0);

/**
 * The photo a strip shows at `slide`, the copies at the ends of the mobile hero's counting as the
 * photos they repeat.
 */
export const toPhotoIndex = (slide: number, amountOfSlides: number, hasLeadingCopy = false) =>
  amountOfSlides > 0
    ? (((slide - firstPhotoSlide(hasLeadingCopy)) % amountOfSlides) + amountOfSlides) % amountOfSlides
    : slide;

const scrollToSlide = (container: HTMLDivElement, slide: number) =>
  container.scroll(container.getBoundingClientRect().width * slide, 0);

/**
 * The real slide a looping strip goes to when it rests on `slide`, or `undefined` when that is a real
 * photo already. A copy of the first photo ends the strip, and a copy of the last one may open it:
 * resting on either copy means the photo it repeats, at the other end of the strip.
 */
const getLoopTarget = (slide: number, amountOfSlides: number, hasLeadingCopy: boolean) => {
  const firstSlide = firstPhotoSlide(hasLeadingCopy);
  if (slide >= amountOfSlides + firstSlide) return firstSlide;
  if (slide < firstSlide) return amountOfSlides;
  return undefined;
};

/**
 * Makes a strip that ends with a copy of its first photo, and may open with a copy of its last, loop
 * both ways: once a swipe comes to rest on a copy, the strip is put on the real photo it repeats
 * without animation. The slides are as wide as the strip, so the two look exactly the same and the
 * reader just carries on swiping (MPH-1213, MPH-1237).
 *
 * Nothing moves while a finger is on the strip: moving it under an active touch fights the browser's
 * own scrolling, and the touch ending is checked as a settle of its own. A finger landing on a copy
 * before the settle is the one exception: the strip is at rest there, and putting it on the real
 * photo first lets that swipe carry on instead of bouncing off the end of the strip.
 */
export function useLoopStrip(
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null> | null,
  amountOfSlides: number,
  enabled: boolean,
  hasLeadingCopy = false
) {
  const previousAmountRef = useRef(amountOfSlides);

  /*
   * The hotel's own photos replace the few borrowed from the results card, and the copy of the first
   * moves to the new end of the strip. A reader resting on the old copy would find its slot turned
   * into the next real photo, so the strip goes back on the first one before that paints.
   */
  useIsomorphicLayoutEffect(() => {
    const previousAmount = previousAmountRef.current;
    previousAmountRef.current = amountOfSlides;
    const container = scrollContainerRef?.current;
    if (!enabled || !container || previousAmount === amountOfSlides || !canLoop(previousAmount)) return;
    const firstSlide = firstPhotoSlide(hasLeadingCopy);
    if (Math.round(getSlidePosition(container)) >= previousAmount + firstSlide) {
      scrollToSlide(container, firstSlide);
    }
  }, [scrollContainerRef, amountOfSlides, enabled, hasLeadingCopy]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!enabled || !container || !canLoop(amountOfSlides)) return undefined;

    let isTouching = false;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    /** `exactly`: only when resting right on a copy, as partway onto it the jump would show. */
    const leaveCopy = (exactly: boolean) => {
      const position = getSlidePosition(container);
      const slide = Math.round(position);
      if (exactly && Math.abs(position - slide) >= 0.01) return;
      const target = getLoopTarget(slide, amountOfSlides, hasLeadingCopy);
      if (target !== undefined) scrollToSlide(container, target);
    };

    const onSettle = () => {
      if (!isTouching) leaveCopy(false);
    };

    const scheduleSettle = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettle, SETTLE_DELAY_MS);
    };

    const onTouchStart = () => {
      isTouching = true;
      clearTimeout(settleTimer);
      leaveCopy(true);
    };

    const onTouchEnd = () => {
      isTouching = false;
      scheduleSettle();
    };

    container.addEventListener('scroll', scheduleSettle, { passive: true });
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    container.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      clearTimeout(settleTimer);
      container.removeEventListener('scroll', scheduleSettle);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [scrollContainerRef, amountOfSlides, enabled, hasLeadingCopy]);
}

type SlideImage = { url: string; sources?: { srcSet: string; media?: string }[] };

/**
 * The photos held ready: up to `upTo` from the start of the list, and `fromEnd` back from its end.
 * `amount` is how long the list was when `fromEnd` was reached.
 */
type ReadyPhotos = { upTo: number; fromEnd: number; amount: number };

/**
 * How far back from the end photos are ready. It only holds for the list it was reached in: once the
 * hotel's own photos replace the few borrowed from the results card, the photos it covered are no
 * longer the last ones, and it starts over on the new last photo, one swipe back from the first.
 */
const getReadyFromEnd = (ready: ReadyPhotos, amountOfSlides: number) =>
  ready.amount === amountOfSlides ? ready.fromEnd : 1;

/**
 * What the mobile hero strip does beyond scrolling, for both heroes (MPH-1213): it holds every photo
 * and loops both ways, only the photo on show is a tab stop or read by screen readers, the arrow keys
 * move between photos, and photos out of reach are placeholders. `isStrip` is false where the hero
 * lays its photos out side by side instead, and then none of it applies.
 *
 * The caller renders `getLoopCopy('end', …)` after the photos when `loop` is set, and
 * `getLoopCopy('start', …)` before them when `hasLeadingCopy` is. It hands `onSlideChanged` and
 * `hasLeadingCopy` to the slider overlay, which tracks the photo on show.
 */
export function useHeroStrip(
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>,
  amountOfSlides: number,
  isStrip: boolean
) {
  const { localization } = usePageWrapperData();
  const [visibleSlide, setVisibleSlide] = useState(0);
  // `fromEnd` is counted back from the end: the last photo is one swipe back from the first.
  const [ready, setReady] = useState<ReadyPhotos>({ upTo: PHOTOS_AHEAD, fromEnd: 1, amount: amountOfSlides });
  const loop = isStrip && canLoop(amountOfSlides);

  /*
   * The copy of the last photo goes in once the page is live, never in the server render: ahead of
   * the first photo it would open the strip on the copy, or have to start it scrolled, and either
   * costs the hero its first paint. It goes in with the strip moved on by one slide before the frame
   * paints, so nothing on screen changes (MPH-1237).
   *
   * The server-rendered strip already swipes before the page is live, so the copy waits for it to be
   * at rest: moving it under a finger or a swipe still coasting fights the browser's own scrolling,
   * and where the browser wins the copy shows in place of the photo the reader was on. It waits for
   * the first photo to have loaded too, so the last one, which loads with the copy, takes no
   * bandwidth from the hero's. Taking the copy out again does not wait: the strip is no strip then.
   */
  const [hasLeadingCopy, setHasLeadingCopy] = useState(false);
  const photoBeforeLeadingChangeRef = useRef<number | undefined>(undefined);
  const firstSlide = firstPhotoSlide(hasLeadingCopy);

  useEffect(() => {
    if (loop === hasLeadingCopy) return undefined;
    const container = scrollContainerRef.current;

    const recordPhoto = () => {
      photoBeforeLeadingChangeRef.current = container
        ? Math.round(getSlidePosition(container)) - firstPhotoSlide(hasLeadingCopy)
        : 0;
    };

    if (!loop || !container) {
      recordPhoto();
      setHasLeadingCopy(loop);
      return undefined;
    }

    // The first slide's photo, there being no copy ahead of it yet.
    const firstPhoto = container.querySelector('img');
    let isTouching = false;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const insertCopy = () => {
      if (isTouching || (firstPhoto && !firstPhoto.complete)) return;
      recordPhoto();
      // Committed right away, so no swipe can start between reading where the strip is and moving it.
      flushSync(() => setHasLeadingCopy(true));
    };

    const scheduleInsert = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(insertCopy, SETTLE_DELAY_MS);
    };

    const onTouchStart = () => {
      isTouching = true;
      clearTimeout(settleTimer);
    };

    const onTouchEnd = () => {
      isTouching = false;
      scheduleInsert();
    };

    container.addEventListener('scroll', scheduleInsert, { passive: true });
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    container.addEventListener('touchcancel', onTouchEnd, { passive: true });
    // A photo that fails to load counts as complete too, so the copy still goes in.
    firstPhoto?.addEventListener('load', scheduleInsert);
    firstPhoto?.addEventListener('error', scheduleInsert);
    scheduleInsert();

    return () => {
      clearTimeout(settleTimer);
      container.removeEventListener('scroll', scheduleInsert);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
      firstPhoto?.removeEventListener('load', scheduleInsert);
      firstPhoto?.removeEventListener('error', scheduleInsert);
    };
  }, [loop, hasLeadingCopy, scrollContainerRef]);

  useIsomorphicLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const photo = photoBeforeLeadingChangeRef.current;
    photoBeforeLeadingChangeRef.current = undefined;
    if (container && photo !== undefined) scrollToSlide(container, Math.max(photo, 0) + firstSlide);
  }, [firstSlide, scrollContainerRef]);

  useLoopStrip(scrollContainerRef, amountOfSlides, loop, hasLeadingCopy);

  const onSlideChanged = useCallback(
    (slide: number) => {
      setVisibleSlide(slide);
      /*
       * Swiping on from the first photo reaches into the start of the list and swiping back from it
       * into the end: whichever end the reader is in holds more photos ready. Only the end the
       * backward loop reached is kept from growing the start, so a photo landed on any other way
       * still gets the photos after it.
       */
      setReady((previous) => {
        const previousFromEnd = getReadyFromEnd(previous, amountOfSlides);
        const inEnd = hasLeadingCopy && slide >= amountOfSlides - previousFromEnd;
        const upTo = slide <= previous.upTo || !inEnd ? Math.max(previous.upTo, slide + PHOTOS_AHEAD) : previous.upTo;
        const fromEnd = inEnd ? Math.max(previousFromEnd, amountOfSlides - slide + PHOTOS_AHEAD) : previousFromEnd;
        return upTo === previous.upTo && fromEnd === previous.fromEnd && amountOfSlides === previous.amount
          ? previous
          : { upTo, fromEnd, amount: amountOfSlides };
      });
    },
    [amountOfSlides, hasLeadingCopy]
  );

  /*
   * The end of the list only counts once the copy of the last photo is in: until then it is out of
   * reach, and the server render must not load it alongside the hero.
   */
  const isPhotoReady = (index: number) =>
    !isStrip ||
    index <= ready.upTo ||
    (hasLeadingCopy && index >= amountOfSlides - getReadyFromEnd(ready, amountOfSlides));

  const getSlideImage = (index: number, image: SlideImage): SlideImage =>
    isPhotoReady(index) ? image : { url: `${localization.imageBasePath}empty_placeholder.svg` };

  /*
   * The copy at either end of a looping strip: the one closing it repeats the first photo and the one
   * opening it the last. Neither is a tab stop or read by screen readers, and clicking one opens the
   * gallery on the photo it repeats. The opening copy is one swipe back from the first photo, so it
   * loads with it.
   */
  const getLoopCopy = (end: 'start' | 'end', images: SlideImage[], onClick: (index: number) => void) => {
    const index = end === 'start' ? images.length - 1 : 0;
    return {
      url: images[index].url,
      sources: images[index].sources,
      loading: end === 'start' ? ('eager' as const) : ('lazy' as const),
      'aria-hidden': true,
      tabIndex: -1,
      onClick: () => onClick(index),
    };
  };

  /*
   * Only the photo on show is a tab stop, as in `ImageSlider` (MPH-243), and the others are hidden
   * from screen readers too: `tabIndex` alone leaves every photo in what VoiceOver and TalkBack read
   * out ahead of the hotel's name. The rest stay reachable through the arrow keys, the counter and
   * the gallery.
   */
  const getSlideAccessibility = (index: number) =>
    !isStrip || index === visibleSlide ? { tabIndex: 0 } : { tabIndex: -1, 'aria-hidden': true };

  const onSlideKeyDown = (index: number) => (event: React.KeyboardEvent) => {
    const container = scrollContainerRef.current;
    if (!isStrip || !container || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
    event.preventDefault();

    const target = index + (event.key === 'ArrowRight' ? 1 : -1);
    if (target < -firstSlide || target > (loop ? amountOfSlides : amountOfSlides - 1)) return;

    container.scrollTo({
      left: container.getBoundingClientRect().width * (target + firstSlide),
      behavior: 'smooth',
    });
    // The copies at the ends of a looping strip are no tab stops: focus the photo a copy repeats,
    // where the strip lands once it settles, so what has focus and what is on show agree. Counted as
    // on show right away rather than once the scroll gets there, so focus never lands on a hidden photo.
    const photo = (target + amountOfSlides) % amountOfSlides;
    onSlideChanged(photo);
    (container.children[photo + firstSlide] as HTMLElement | undefined)?.focus({ preventScroll: true });
  };

  return {
    loop,
    hasLeadingCopy,
    getLoopCopy,
    onSlideChanged,
    isPhotoReady,
    getSlideImage,
    getSlideAccessibility,
    onSlideKeyDown,
  };
}
